import { SbbTimeInputElement } from "@sbb-esta/lyne-elements/time-input.js";
import { createLogger } from "logging";
import { RefObject, useCallback, useState } from "react";

const logger = createLogger("useTimeInputSuggestions");

const hourOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"));
const minuteOptions = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, "0"));

export function useTimeInputSuggestions(
  externalRef: RefObject<SbbTimeInputElement | null>,
  onChangeTime: (value: string) => void
) {
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [visible, setVisible] = useState(false);
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const [selectedMinute, setSelectedMinute] = useState<string | null>(null);

  const computeAnchor = useCallback(() => {
    const host = externalRef?.current;
    if (!host) return;
    try {
      setAnchorRect(host.getBoundingClientRect());
    } catch (err) {
      logger.debug("computeAnchor: failed to get bounding rect", err as Error);
      setAnchorRect(null);
    }
  }, [externalRef]);

  const openTimePanel = useCallback(() => {
    computeAnchor();
    setVisible(true);
  }, [computeAnchor]);

  const closeTimePanel = useCallback(() => {
    setVisible(false);
    setSelectedHour(null);
    setSelectedMinute(null);
  }, []);

  const selectHours = (h: string) => {
    setSelectedHour(h);
    if (!h || !selectedMinute) {
      return;
    }
    const newTime = `${h}:${selectedMinute}`;
    onChangeTime(newTime);
    closeTimePanel();
  };

  const selectMinutes = (m: string) => {
    setSelectedMinute(m);
    if (!selectedHour || !m) {
      return;
    }
    const newTime = `${selectedHour}:${m}`;
    onChangeTime(newTime);
    closeTimePanel();
  };

  return {
    openTimePanel,
    closeTimePanel,
    hourOptions,
    minuteOptions,
    anchorRect,
    visible,
    selectHours,
    selectedHour,
    selectMinutes,
    selectedMinute,
  };
}

export default useTimeInputSuggestions;
